/**
 * Runtime contract injected by the ASP.NET/DNN host into each React root.
 *
 * A page may contain more than one DynamicForms module, therefore configuration
 * is read per root element when possible. The fallback sources are kept for
 * standalone ASPX pages and older host markup.
 */
export type RuntimeMode = 'management' | 'form-view' | 'edit-submission' | 'report-view' | 'custom-report-view' | 'my-submissions';
export type BootstrapVersion = '3' | '5';

export interface DynamicFormsRuntimeConfig {
  mode: RuntimeMode;
  formId?: number;
  submittedFormId?: number;
  reportId?: number;
  serverDateTime?: string | number;
  formName?: string;
  editFormTabId?: string;
  culture?: string;
  baseUrl?: string;
  bootstrapVersion?: BootstrapVersion;
  moduleId?: number;
  pageSize?: number;
  showStatus?: boolean;
  showPrint?: boolean;
}

declare global {
  interface Window { __DF_REACT_CONFIG__?: DynamicFormsRuntimeConfig; }
}

function numberValue(value?: string): number | undefined {
  if (!value) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

/** Returns the only supported Bootstrap values; every unknown value safely falls back to Bootstrap 3. */
export function normalizeBootstrapVersion(value?: string | number): BootstrapVersion {
  return String(value ?? '').trim() === '5' ? '5' : '3';
}

/**
 * Reads runtime configuration using this precedence:
 * 1) JSON script element emitted by the server, 2) current root data-* attributes,
 * 3) legacy window global. The result is normalized before use.
 */
export function getRuntimeConfig(rootElement?: HTMLElement): DynamicFormsRuntimeConfig {
  const script = document.getElementById('df-react-config');
  if (script?.textContent) {
    try {
      const parsed = JSON.parse(script.textContent) as DynamicFormsRuntimeConfig;
      return { ...parsed, bootstrapVersion: normalizeBootstrapVersion(parsed.bootstrapVersion) };
    } catch (error) {
      console.error('Unable to parse DynamicForms React runtime config.', error);
    }
  }
  const root = rootElement ?? document.querySelector<HTMLElement>('[data-dynamicforms-react-root]');
  if (root) {
    const dataset = root.dataset;
    return {
      mode: (dataset.mode as RuntimeMode) || 'management',
      formId: numberValue(dataset.formId),
      submittedFormId: numberValue(dataset.submittedFormId),
      reportId: numberValue(dataset.reportId),
      serverDateTime: dataset.serverDateTime,
      formName: dataset.formName,
      editFormTabId: dataset.editFormTabId,
      culture: dataset.culture,
      baseUrl: dataset.baseUrl,
      bootstrapVersion: normalizeBootstrapVersion(dataset.bootstrapVersion),
      moduleId: numberValue(dataset.moduleId),
      pageSize: numberValue(dataset.pageSize),
      showStatus: dataset.showStatus !== 'false',
      showPrint: dataset.showPrint !== 'false'
    };
  }
  const fallback = window.__DF_REACT_CONFIG__ ?? { mode: 'management' as const };
  return { ...fallback, bootstrapVersion: normalizeBootstrapVersion(fallback.bootstrapVersion) };
}
